/**
 * Persists per-offer reward selections in `localStorage`, scoped to the
 * current event id. When the event rolls over (new id from server) the
 * stored selections are wiped — the player picks fresh next event.
 *
 * Shape on disk:
 *   { "eventId": "...", "selections": { "basic": "tgem-25", "ads": null } }
 */

import { type ScratchOfferId } from '@/common/types/scratch';

const KEY = 'scratch_selections_v1';

interface Stored {
  eventId: string;
  selections: Partial<Record<ScratchOfferId, string | null>>;
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);

    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;

    if (!parsed || typeof parsed.eventId !== 'string') return null;

    return parsed;
  } catch {
    return null;
  }
}

function write(value: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* localStorage unavailable / quota exceeded — ignore. */
  }
}

function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

const scratchSelectionStorage = {
  /** Selections for the given event, or null if storage is empty / stale. */
  getForEvent(
    eventId: string | null,
  ): Partial<Record<ScratchOfferId, string | null>> | null {
    if (!eventId) return null;
    const stored = read();

    if (!stored || stored.eventId !== eventId) return null;

    return stored.selections;
  },

  /** Save / overwrite the player's pick for one offer in the current event. */
  setSelection(
    eventId: string,
    offerId: ScratchOfferId,
    rewardKey: string | null,
  ): void {
    if (!eventId) return;
    const existing = read();
    const selections =
      existing?.eventId === eventId
        ? { ...existing.selections, [offerId]: rewardKey }
        : { [offerId]: rewardKey };
    write({ eventId, selections });
  },

  /**
   * Wipe storage if the saved event id is missing or different from the
   * one passed in — called on every hydrate to prune stale data.
   */
  pruneIfStale(eventId: string | null): void {
    const stored = read();

    if (!stored) return;

    if (!eventId || stored.eventId !== eventId) clear();
  },
};

export { scratchSelectionStorage };
